import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { Alert, Box, Button, Chip, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';

import {
  evaluateGuidedMode,
  getGuidedModeDefaults,
  getGuidedQuestionsForDisplay,
  getMultiLocationQuoteSpec,
  GuidedModeAnswers,
  GuidedModePath,
  GuidedModeRecommendation,
  isMultiLocationGuidedQuoteService,
} from '../../lib/services/guidedMode';
import type { MarketplaceService } from '../../types/pricing';

interface GuidedModePanelProps {
  service: MarketplaceService;
  // eslint-disable-next-line no-unused-vars
  onRecommendationChange(nextRecommendation: GuidedModeRecommendation): void;
}

const pathCards: Array<{ path: GuidedModePath; title: string; body: string; icon: React.ReactNode }> = [
  {
    path: 'standard_package',
    title: 'Standard Package',
    body: 'Use the default managed IT package and confirm environment details after checkout.',
    icon: <CheckCircleOutline />,
  },
  {
    path: 'help_me_choose',
    title: 'Help Me Choose',
    body: 'Answer a few questions so Aithon can recommend the right starting path.',
    icon: <HelpOutline />,
  },
];

const fallbackPainOptions = [
  ['slow_response', 'Slow support response'],
  ['security_risk', 'Security risk'],
  ['no_strategy', 'No IT strategy'],
  ['tool_sprawl', 'Tool sprawl'],
  ['unclear_ownership', 'Unclear ownership'],
];

function optionValue(option: any): string {
  return typeof option === 'string' ? option : String(option?.value || option?.key || option?.id || '');
}

function optionLabel(option: any): string {
  return typeof option === 'string' ? option : String(option?.label || option?.name || option?.value || option?.key || '');
}

function questionOptions(question: any): any[] {
  return Array.isArray(question?.options) ? question.options : [];
}

function renderDynamicQuestion(
  question: any,
  answers: GuidedModeAnswers,
  // eslint-disable-next-line no-unused-vars
  update: (_patch: Partial<GuidedModeAnswers>) => void,
) {
  const key = String(question?.key || '');
  if (!key) return null;
  const label = String(question?.label || key);
  const type = String(question?.type || 'text');
  const value = (answers as any)[key];
  const options = questionOptions(question);

  if (type === 'number') {
    return (
      <TextField
        key={key}
        label={label}
        type="number"
        size="small"
        value={value ?? ''}
        onChange={(event) => update({ [key]: Number(event.target.value) } as any)}
        inputProps={{ min: question?.min ?? 1, max: question?.max, step: 1 }}
        helperText={question?.help_text || question?.why_we_ask || undefined}
      />
    );
  }

  if (type === 'multi_select' || type === 'multi-select') {
    const current = Array.isArray(value) ? value : [];
    return (
      <Box key={key}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          {options.map((option) => {
            const nextValue = optionValue(option);
            const selected = current.includes(nextValue);
            return (
              <Chip
                key={nextValue}
                label={optionLabel(option)}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => update({ [key]: selected ? current.filter((entry: string) => entry !== nextValue) : [...current, nextValue] } as any)}
                sx={{ mb: 0.75 }}
              />
            );
          })}
        </Stack>
        {(question?.help_text || question?.why_we_ask) && (
          <Typography variant="caption" color="text.secondary">{question.help_text || question.why_we_ask}</Typography>
        )}
      </Box>
    );
  }

  if (type === 'single_select' || type === 'single-select' || options.length > 0) {
    return (
      <FormControl key={key} size="small" fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={value ?? optionValue(options[0])}
          onChange={(event) => update({ [key]: event.target.value } as any)}
        >
          {options.map((option) => {
            const nextValue = optionValue(option);
            return <MenuItem key={nextValue} value={nextValue}>{optionLabel(option)}</MenuItem>;
          })}
        </Select>
        {(question?.help_text || question?.why_we_ask) && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>{question.help_text || question.why_we_ask}</Typography>
        )}
      </FormControl>
    );
  }

  return (
    <TextField
      key={key}
      label={label}
      size="small"
      value={value ?? ''}
      onChange={(event) => update({ [key]: event.target.value } as any)}
      helperText={question?.help_text || question?.why_we_ask || undefined}
    />
  );
}

function formatCents(cents: number | undefined): string {
  return `$${((Number(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatWholeCents(cents: number | undefined): string {
  return `$${((Number(cents) || 0) / 100).toLocaleString('en-US')}`;
}

function siteKey(site: any): string {
  return String(site?.site_key || site?.key || site?.id || site?.name || site?.label || 'site');
}

function optionKey(option: any): string {
  return String(option?.option_key || option?.key || option?.id || '');
}

function quoteOptionLabel(option: any): string {
  const provider = option.provider || option.carrier || option.access_provider || 'Provider';
  const bandwidth = option.bandwidth_label || (option.bandwidth_mbps ? `${option.bandwidth_mbps}M` : 'Bandwidth');
  const accessType = option.access_type ? ` ${option.access_type}` : '';
  return `${provider} ${bandwidth}${accessType}`.trim();
}

function componentEntries(value: any): Array<[string, number]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return Object.entries(value)
    .map(([label, cents]) => [label, Math.max(0, Math.round(Number(cents) || 0))] as [string, number])
    .filter(([, cents]) => cents > 0);
}

function labelize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

const NO_OPTION_KEY = '__none__';

function connectionSlots(site: any): any[] {
  return Array.isArray(site.connection_slots) ? site.connection_slots : [];
}

function connectionSlotKey(slot: any, index: number): string {
  return String(slot?.key || slot?.slot_key || `provider_${index + 1}`);
}

function optionsForSlot(site: any, slot: any): any[] {
  const options = Array.isArray(site.options) ? site.options : [];
  const allowedKeys = Array.isArray(slot?.option_keys) ? slot.option_keys.map(String) : [];
  if (allowedKeys.length === 0) return options;
  return allowedKeys
    .map((key: string) => options.find((option: any) => optionKey(option) === key))
    .filter(Boolean);
}

function defaultOptionKeyForSlot(site: any, slot: any): string {
  const options = optionsForSlot(site, slot);
  const defaultKey = String(slot?.default_option_key || slot?.recommended_option_key || '');
  const selected = options.find((option: any) => optionKey(option) === defaultKey) || options.find((option: any) => option?.recommended) || options[0];
  return selected ? optionKey(selected) : NO_OPTION_KEY;
}

function selectedConnectionRows(site: any, siteSelection: Record<string, string> | undefined) {
  return connectionSlots(site).map((slot: any, index: number) => {
    const slotKey = connectionSlotKey(slot, index);
    const options = optionsForSlot(site, slot);
    const selectedKey = siteSelection?.[slotKey] ?? defaultOptionKeyForSlot(site, slot);
    const selectedOption = selectedKey === NO_OPTION_KEY ? null : options.find((option: any) => optionKey(option) === selectedKey) || options[0] || null;
    return { slot, slotKey, options, selectedKey, selectedOption };
  });
}

function optionPrice(option: any): { mrc: number; nrc: number } {
  if (!option) return { mrc: 0, nrc: 0 };
  return {
    mrc: Math.max(0, Number(option.mrc_cents || option.monthly_cents || 0)),
    nrc: Math.max(0, Number(option.nrc_cents || option.one_time_cents || 0)),
  };
}

function renderSelectedConnectionDetails(connections: Array<{ slot: any; slotKey: string; selectedOption: any }>) {
  const selectedConnections = connections.filter((connection) => connection.selectedOption);
  if (selectedConnections.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>Selected circuit details</Typography>
      <Grid container spacing={1.5}>
        {selectedConnections.map(({ slot, slotKey, selectedOption }) => {
          const mrcComponents = componentEntries(selectedOption.mrc_components_cents);
          const oneTimeComponents = componentEntries(selectedOption.one_time_components_cents || selectedOption.nrc_components_cents);
          const includedEquipment = Array.isArray(selectedOption.included_equipment)
            ? selectedOption.included_equipment
            : Array.isArray(selectedOption.includes)
              ? selectedOption.includes
              : [];
          const addOns = Array.isArray(selectedOption.add_ons)
            ? selectedOption.add_ons
            : Array.isArray(selectedOption.addons)
              ? selectedOption.addons
              : [];
          const { mrc, nrc } = optionPrice(selectedOption);

          return (
            <Grid item xs={12} md={6} key={slotKey}>
              <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="caption" color="primary" sx={{ fontWeight: 900 }}>{slot.label || labelize(slotKey)}</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{quoteOptionLabel(selectedOption)}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">Monthly</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800 }}>{formatCents(mrc)}/mo</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">One-time</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800 }}>{formatCents(nrc)}</Typography>
                </Box>
                {(mrcComponents.length > 0 || oneTimeComponents.length > 0) && (
                  <Stack spacing={0.25} sx={{ mb: 0.75 }}>
                    {mrcComponents.map(([label, cents]) => (
                      <Box key={`${slotKey}-${label}-mrc`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="caption">{labelize(label)}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCents(cents)}/mo</Typography>
                      </Box>
                    ))}
                    {oneTimeComponents.map(([label, cents]) => (
                      <Box key={`${slotKey}-${label}-nrc`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="caption">{labelize(label)}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCents(cents)}</Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
                {(includedEquipment.length > 0 || addOns.length > 0) && (
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                    {includedEquipment.map((item: any) => <Chip key={`${slotKey}-${String(item)}`} size="small" label={String(item)} />)}
                    {addOns.map((item: any) => <Chip key={`${slotKey}-${String(item?.key || item?.name || item)}`} size="small" color="secondary" variant="outlined" label={String(item?.name || item?.label || item)} />)}
                  </Stack>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

function renderMultiLocationQuote(
  quote: Record<string, any>,
  answers: GuidedModeAnswers,
  // eslint-disable-next-line no-unused-vars
  update: (_patch: Partial<GuidedModeAnswers>) => void,
) {
  const sites = Array.isArray(quote.sites) ? quote.sites : [];
  const legacySelections = answers.site_selections || {};
  const connectionSelections = answers.site_connection_selections || {};
  const selectedRows = sites.map((site: any) => {
    const key = siteKey(site);
    const slots = connectionSlots(site);

    if (slots.length > 0) {
      const connections = selectedConnectionRows(site, connectionSelections[key]);
      const totalMrc = connections.reduce((sum, connection) => sum + optionPrice(connection.selectedOption).mrc, 0);
      const totalNrc = connections.reduce((sum, connection) => sum + optionPrice(connection.selectedOption).nrc, 0);
      return { site, siteKey: key, connections, totalMrc, totalNrc, selectedCircuitCount: connections.filter((connection) => connection.selectedOption).length };
    }

    const options = Array.isArray(site.options) ? site.options : [];
    const selectedKey = legacySelections[key] || optionKey(options.find((option: any) => option?.recommended)) || optionKey(options[0]);
    const selectedOption = selectedKey === NO_OPTION_KEY ? null : options.find((option: any) => optionKey(option) === selectedKey) || options[0] || null;
    const { mrc, nrc } = optionPrice(selectedOption);
    return { site, siteKey: key, connections: [{ slot: { label: 'Provider 1' }, slotKey: 'provider_1', options, selectedKey, selectedOption }], totalMrc: mrc, totalNrc: nrc, selectedCircuitCount: selectedOption ? 1 : 0 };
  });
  const totalMrc = selectedRows.reduce((sum, row) => sum + row.totalMrc, 0);
  const totalNrc = selectedRows.reduce((sum, row) => sum + row.totalNrc, 0);
  const selectedCircuitCount = selectedRows.reduce((sum, row) => sum + row.selectedCircuitCount, 0);
  const configuredSiteCount = selectedRows.filter((row) => row.selectedCircuitCount > 0).length;
  const termMonths = Number(quote.term_months || answers.term_months || 36);

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} lg={8}>
        <Stack spacing={2}>
          {selectedRows.map(({ site, siteKey: key, connections, totalMrc: siteMrc, totalNrc: siteNrc, selectedCircuitCount: siteCircuitCount }, index) => (
            <Box key={key} sx={{ p: { xs: 1.5, md: 2 }, border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1.5, alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 900, letterSpacing: 0.8 }}>
                    LOCATION {index + 1}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>{site.name || key}</Typography>
                  {site.address && <Typography variant="body2" color="text.secondary">{site.address}</Typography>}
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="caption" color="text.secondary">Selected</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{siteCircuitCount > 0 ? `${formatWholeCents(siteMrc)}/mo` : 'Skipped'}</Typography>
                  {siteCircuitCount > 0 && <Typography variant="caption" color="text.secondary">{formatCents(siteNrc)} one-time</Typography>}
                </Box>
              </Box>

              <Grid container spacing={1.5}>
                {connections.map(({ slot, slotKey, options, selectedKey, selectedOption }, slotIndex) => {
                  const nextSiteSelections = connectionSelections[key] || {};
                  const defaultRole = slot.default_role || (slotIndex === 0 ? 'Primary' : 'Secondary');
                  const { mrc, nrc } = optionPrice(selectedOption);
                  return (
                    <Grid item xs={12} md={6} key={slotKey}>
                      <Box sx={{ height: '100%', p: 1.5, border: '1px solid', borderColor: selectedOption ? 'primary.light' : 'divider', borderRadius: 2, bgcolor: selectedOption ? 'primary.50' : 'grey.50' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{slot.label || `Provider ${slotIndex + 1}`}</Typography>
                            <Typography variant="caption" color="text.secondary">{defaultRole} / diverse path</Typography>
                          </Box>
                          {selectedOption && optionKey(selectedOption) === String(slot.default_option_key || '') && <Chip size="small" color="primary" label="Default" />}
                        </Box>
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                          <InputLabel>{slot.label || `Provider ${slotIndex + 1}`}</InputLabel>
                          <Select
                            label={slot.label || `Provider ${slotIndex + 1}`}
                            value={selectedKey || NO_OPTION_KEY}
                            onChange={(event) => update({
                              site_connection_selections: {
                                ...connectionSelections,
                                [key]: {
                                  ...nextSiteSelections,
                                  [slotKey]: String(event.target.value),
                                },
                              },
                            })}
                          >
                            <MenuItem value={NO_OPTION_KEY}>No circuit / skip</MenuItem>
                            {options.map((option: any) => {
                              const keyForOption = optionKey(option);
                              return <MenuItem key={keyForOption} value={keyForOption}>{quoteOptionLabel(option)} — {formatCents(optionPrice(option).mrc)}/mo</MenuItem>;
                            })}
                          </Select>
                        </FormControl>
                        {selectedOption ? (
                          <>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                              {(selectedOption.bandwidth_label || selectedOption.bandwidth_mbps) && <Chip size="small" label={selectedOption.bandwidth_label || `${selectedOption.bandwidth_mbps} Mbps`} />}
                              {selectedOption.access_type && <Chip size="small" label={selectedOption.access_type} />}
                              <Chip size="small" color="success" label="Selected" />
                            </Stack>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="text.secondary">MRC</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 900 }}>{formatCents(mrc)}/mo</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="text.secondary">NRC</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCents(nrc)}</Typography>
                            </Box>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Leave blank if this site only needs one circuit or should be skipped.
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {renderSelectedConnectionDetails(connections)}
            </Box>
          ))}
        </Stack>
      </Grid>
      <Grid item xs={12} lg={4}>
        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 96 }, p: 2, border: '1px solid', borderColor: selectedCircuitCount > 0 ? 'primary.main' : 'warning.main', borderRadius: 3, bgcolor: 'background.paper', boxShadow: 2 }}>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>Quote summary</Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>{configuredSiteCount} locations / {selectedCircuitCount} circuits configured</Typography>
          <Stack spacing={0.75} sx={{ mb: 1.5 }}>
            {selectedRows.map(({ site, siteKey: key, totalMrc: siteMrc, selectedCircuitCount: siteCircuitCount }) => (
              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                <Typography variant="body2" color="text.secondary">{site.name || key}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{siteCircuitCount > 0 ? `${formatCents(siteMrc)}/mo` : 'Skipped'}</Typography>
              </Box>
            ))}
          </Stack>
          <Divider sx={{ my: 1.25 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Total monthly</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{formatCents(totalMrc)}/mo</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" color="text.secondary">Total one-time</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCents(totalNrc)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.25 }}>
            <Typography variant="body2" color="text.secondary">Term</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{termMonths} months</Typography>
          </Box>
          <Alert severity={selectedCircuitCount > 0 ? 'info' : 'warning'} sx={{ mt: 1 }}>
            <Typography variant="caption">
              {selectedCircuitCount > 0
                ? 'Checkout preserves every selected site, circuit, component, and add-on. You can skip a second circuit or an entire site by choosing No circuit.'
                : 'Choose at least one circuit anywhere in the quote before adding it to cart.'}
            </Typography>
          </Alert>
        </Box>
      </Grid>
    </Grid>
  );
}

const GuidedModePanel: React.FC<GuidedModePanelProps> = ({ service, onRecommendationChange }) => {
  const defaults = useMemo(() => getGuidedModeDefaults(service), [service]);
  const [answers, setAnswers] = useState<GuidedModeAnswers>(defaults);
  const questions = useMemo(() => getGuidedQuestionsForDisplay(service), [service]);
  const isMultiLocationQuote = useMemo(() => isMultiLocationGuidedQuoteService(service), [service]);
  const multiLocationQuote = useMemo(() => getMultiLocationQuoteSpec(service), [service]);
  const hasSchemaQuestions = questions.length > 0;

  const recommendation = useMemo(() => evaluateGuidedMode(service, answers), [service, answers]);

  React.useEffect(() => {
    onRecommendationChange(recommendation);
  }, [onRecommendationChange, recommendation]);

  const update = (patch: Partial<GuidedModeAnswers>) => setAnswers((current) => ({ ...current, ...patch }));

  const togglePain = (key: string) => {
    const current = answers.support_pain || [];
    update({ support_pain: current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key] });
  };

  const monthly = recommendation.monthlyCents ? `$${(recommendation.monthlyCents / 100).toLocaleString('en-US')}/mo` : 'Finalized after review';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, mb: 3, border: '1px solid', borderColor: 'primary.light', borderRadius: 3, bgcolor: 'primary.50' }}>
      <Typography variant="overline" color="primary" sx={{ fontWeight: 900, letterSpacing: 1 }}>
        Guided Mode
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.75 }}>
        {isMultiLocationQuote ? 'Build the multi-site quote' : 'Choose how to configure this service'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isMultiLocationQuote
          ? 'Select Provider 1 and Provider 2 per location, or choose No circuit to skip a slot/site. Aithon keeps every selected circuit inside one BCN-managed cart item.'
          : 'Technical services should not become lead forms. Pick the standard package or answer a few questions and we’ll carry the recommendation into checkout.'}
      </Typography>

      {!isMultiLocationQuote && <Grid container spacing={1.25} sx={{ mb: 2 }}>
        {pathCards.map((card) => {
          const selected = answers.path === card.path;
          return (
            <Grid item xs={12} key={card.path}>
              <Button
                fullWidth
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => update({ path: card.path })}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 1.5, borderRadius: 2 }}
                startIcon={card.icon}
              >
                <Box>
                  <Typography sx={{ fontWeight: 900 }}>{card.title}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: selected ? 'primary.contrastText' : 'text.secondary' }}>{card.body}</Typography>
                </Box>
              </Button>
            </Grid>
          );
        })}
      </Grid>}

      {isMultiLocationQuote && renderMultiLocationQuote(multiLocationQuote, answers, update)}

      {!isMultiLocationQuote && answers.path === 'help_me_choose' && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {hasSchemaQuestions ? questions.map((question) => renderDynamicQuestion(question, answers, update)) : (
            <>
              <TextField
                label="Supported users"
                type="number"
                size="small"
                value={answers.supported_users}
                onChange={(event) => update({ supported_users: Number(event.target.value) })}
                inputProps={{ min: 1, step: 1 }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Internal IT owner?</InputLabel>
                <Select
                  label="Internal IT owner?"
                  value={answers.internal_it_owner || 'partial'}
                  onChange={(event) => update({ internal_it_owner: event.target.value as any })}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="partial">Partial / shared</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Productivity platform</InputLabel>
                <Select
                  label="Productivity platform"
                  value={answers.productivity_platform || 'microsoft_365'}
                  onChange={(event) => update({ productivity_platform: event.target.value })}
                >
                  <MenuItem value="microsoft_365">Microsoft 365</MenuItem>
                  <MenuItem value="google_workspace">Google Workspace</MenuItem>
                  <MenuItem value="mixed">Mixed</MenuItem>
                  <MenuItem value="not_sure">Not sure</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Security baseline</InputLabel>
                <Select
                  label="Security baseline"
                  value={answers.security_baseline || 'managed'}
                  onChange={(event) => update({ security_baseline: event.target.value })}
                >
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="managed">Managed</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                  <MenuItem value="not_sure">Not sure</MenuItem>
                </Select>
              </FormControl>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Main pain points
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {fallbackPainOptions.map(([key, label]) => {
                    const selected = (answers.support_pain || []).includes(key);
                    return (
                      <Chip
                        key={key}
                        label={label}
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => togglePain(key)}
                        sx={{ mb: 0.75 }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      )}

      <Alert severity={recommendation.outcome === 'signature_support_standard_package' || recommendation.outcome === 'multi_location_quote_ready' ? 'success' : 'info'} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 900 }}>{recommendation.title}</Typography>
        <Typography variant="body2">{recommendation.summary}</Typography>
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">Recommendation estimate</Typography>
        <Typography variant="body2" sx={{ fontWeight: 900 }}>{monthly}</Typography>
      </Box>
      {recommendation.reasons.length > 0 && (
        <Stack spacing={0.5}>
          {recommendation.reasons.slice(0, 3).map((reason) => (
            <Typography key={reason} variant="caption" color="text.secondary">• {reason}</Typography>
          ))}
        </Stack>
      )}
      {questions.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
          Guided spec loaded from order schema · {questions.length} supplier-authored questions
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(GuidedModePanel);
